<template>
  <section class="section bg-light text-center">
    <div class="container">
      <h2>Woran liegt und wie wahrscheinlich ist es, dass ich berufsunfähig werde?</h2>
      <div class="flex flex-wrap -mx-4 mt-7 print:block overflow-hidden">
        <div class="w-full lg:w-1/2 px-4 mb-14 lg:mb-0">
          <h4>Wahrscheinlichkeit, bis zur Rente mit 65 berufsunfähig zu werden nach Altersgruppen</h4>
          <div class="flex flex-wrap -ml-4 mt-7 mr-1.5 md:mr-0 text-left">
            <div class="w-full md:w-5/6 px-4">
              <div
                v-for="{name, gender: { women, men }} in ageGroupStatistics"
                :key="name"
                class="flex flex-wrap -mx-4"
              >
                <div
                  class="w-1/4 px-4 text-right self-center"
                  v-text="name"
                />
                <div class="w-3/4 pr-4 py-2.5">
                  <div
                    class="bg-secondary text-white mb-1.5 py-1.5 px-2.5 antialiased"
                    :style="{width: `${women * 2}%` }"
                    v-text="`${women}%`"
                  />
                  <div
                    class="bg-dark text-white py-1.5 px-2.5 antialiased"
                    :style="{width: `${men * 2}%` }"
                    v-text="`${men}%`"
                  />
                </div>
              </div>
              <!-- legend and axis -->
              <div class="flex flex-wrap -mx-4">
                <div class="w-3/4 ml-[25%] p-0">
                  <hr class="border-dark m-0">
                </div>
              </div>
              <div class="flex flex-wrap -mx-4">
                <div class="w-3/4 ml-[25%] p-0 flex justify-between mt-2.5">
                  <div>0%</div>
                  <div>10%</div>
                  <div>20%</div>
                  <div>30%</div>
                  <div>40%</div>
                  <div>50%</div>
                </div>
              </div>
              <div class="flex flex-wrap -mx-4 mt-2.5">
                <div class="w-3/4 ml-[25%] pr-4">
                  <!-- eslint-disable vue/no-v-html -->
                  <div class="flex">
                    <div class="mr-14">
                      <div>
                        <div
                          class="px-1.5 antialiased bg-secondary"
                          v-html="'&emsp;'"
                        />
                      </div>
                      <div class="ml-2.5 self-center">
                        Frauen
                      </div>
                    </div>
                    <div>
                      <div>
                        <div
                          class="px-1.5 antialiased bg-dark"
                          v-html="'&emsp;'"
                        />
                      </div>
                      <div class="ml-2.5 self-center">
                        Männer
                      </div>
                    </div>
                    <!-- eslint-enable vue/no-v-html -->
                  </div>
                </div>
              </div>
              <div class="text-center mt-5">
                <a href="https://de.statista.com/statistik/daten/studie/28002/umfrage/wahrscheinlichkeit-der-berufsunfaehigkeit-bis-zur-rente-mit-65/">Quelle: Statista (2009)</a>
              </div>
            </div>
          </div>
        </div>

        <div class="w-full lg:w-1/2 px-4">
          <h4>Ursachen für Berufsunfähigkeit</h4>
          <SvgVue
            icon="bedarfsinfo/berufsunfaehigkeit/ursachen"
            class="w-1/2 my-7 inline-block"
          />
          <div class="flex flex-wrap -mx-4 text-left pt-2.5">
            <div class="w-full md:w-2/3 lg:flex-1 md:ml-[16.666667%] lg:ml-0 px-4">
              <div class="flex flex-wrap -mx-4">
                <template
                  v-for="{name, percentage} in causes"
                  :key="name"
                >
                  <div class="w-1/6 sm:w-1/12 px-4 text-secondary">
                    <strong>{{ percentage }}%</strong>
                  </div>
                  <div class="w-5/6 sm:w-11/12 md:w-5/12 px-4 mb-2.5">
                    {{ name }}
                  </div>
                </template>
              </div>
            </div>
          </div>
          <div class="text-center mt-2.5">
            <a href="https://de.statista.com/statistik/daten/studie/536354/umfrage/verteilung-der-ursachen-von-berufsunfaehigkeit-in-deutschland/">Quelle: Statista (2022)</a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import SvgVue from "@/application/components/SvgVue";

type Group = {
  name: string,
  gender: {
    men: number,
    women: number,
  }
};

type Cause = {
  name: string,
  percentage: number
};

const ageGroupStatistics: Group[] = [
  { name: '20-jährige', gender: { women: 43, men: 38 } },
  { name: '30-jährige', gender: { women: 41, men: 37 } },
  { name: '40-jährige', gender: { women: 39, men: 35 } },
  { name: '50-jährige', gender: { women: 34, men: 29 } },
];

const causes: Cause[] = [
  { name: 'Nervenkrankheiten', percentage: 32 },
  { name: 'Erkrankung des Skelett- und Bewegungsapparates', percentage: 20 },
  { name: 'Sonstige Erkrankungen', percentage: 18 },
  { name: 'Tumore', percentage: 15 },
  { name: 'Unfälle', percentage: 8 },
  { name: 'Erkrankung des Herzens und des Gefäßsystems', percentage: 7 },
];
</script>
